import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'anime_favorites_v1';
const SYNC_EVENT = 'anime-favorites-changed';

function readFavorites(): number[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((item) => typeof item === 'number');
    }
    return [];
  } catch {
    return [];
  }
}

function writeFavorites(ids: number[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore write errors
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<number[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener(SYNC_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(SYNC_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const persist = useCallback((ids: number[]) => {
    writeFavorites(ids);
    setFavorites(ids);
    window.dispatchEvent(new Event(SYNC_EVENT));
  }, []);

  const toggleFavorite = useCallback(
    (id: number) => {
      const current = readFavorites();
      const next = current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id];
      persist(next);
      return next.includes(id);
    },
    [persist],
  );

  const isFavorite = useCallback((id: number) => favorites.includes(id), [favorites]);

  const clearFavorites = useCallback(() => persist([]), [persist]);

  return { favorites, toggleFavorite, isFavorite, clearFavorites };
}